<template>
  <ModalVue
    :modalActive="isThisModalVisible"
    @close="close"
    :hasHeaderBorder="true"
    :hasFooterBorder="true"
  >
    <Spinner
      v-if="isLoadingSubmit"
      color="primary"
      size="5rem"
      width="0.5rem"
      class="absolute inset-0 flex justify-center items-center bg-white bg-opacity-50 z-50"
    />

    <template #header>
      <ActionModalHeader
        :station-details="stationDetails"
        :title="release ? 'Liberar vaga' : 'Remover médico'"
      />
    </template>

    <div v-if="!showShiftList" class="flex flex-col gap-4">
      <div class="flex flex-col md:flex-row gap-4">
        <UnidentifiedCoverageCard
          v-if="release"
          class="md:max-w-xs w-full"
          :responsible-name="originCoverage.responsibleName"
          :note="originCoverage.note"
          compact
        />
        <CardDutyWithDoctor
          v-if="stationDetails?.doctorStation && !release"
          :formation="stationDetails.doctorStation.dsPaymentDoctorFormation?.name || ''"
          :team="stationDetails.doctorStation.ourTeam ? 'Interno' : 'Externo'"
          :position="stationDetails.position?.name || ''"
          :shift="stationDetails.shift?.name || ''"
          :dutyLabel="stationDetails.doctorStation.type || ''"
          :doctorName="stationDetails.doctorStation.doctor?.mainCrm?.name || 'Nome não encontrado'"
          :doctorCrm="`${stationDetails.doctorStation.doctor?.mainCrm?.number || ''}-${
            stationDetails.doctorStation.doctor?.mainCrm?.uf || ''
          }`"
          :active="false"
          :scheduleId="stationDetails.schedule?.id"
          :npsScore="stationDetails?.doctorNpsScore?.npsScore"
          :avatarUrl="stationDetails.doctorStation.doctor?.user?.avatarUrl"
        />

        <div class="grid grid-cols-2 gap-x-4 gap-y-2 text-left text-sm text-textPrimary">
          <div class="flex flex-col">
            <span class="font-semibold">Empresa</span>
            <span>{{ stationDetails?.schedule?.healthUnit?.company?.name || 'N/A' }}</span>
          </div>
          <div class="flex flex-col">
            <span class="font-semibold">Escala</span>
            <span>{{ stationDetails?.schedule?.name || 'N/A' }}</span>
          </div>
          <div class="flex flex-col">
            <span class="font-semibold">Unidade</span>
            <span>{{ stationDetails?.schedule?.healthUnit?.name || 'N/A' }}</span>
          </div>
          <div class="flex flex-col">
            <span class="font-semibold">Posição</span>
            <span>{{ stationDetails?.position?.name || 'N/A' }}</span>
          </div>
        </div>
      </div>

      <CoverageModeSelector
        v-if="!release && scheduleSupports && (allowUnidentified || unidentifiedLocked)"
        v-model="replacement"
        variant="cards"
        label="O que fica no lugar?"
        :options="[CoverageMode.OPEN, CoverageMode.UNIDENTIFIED]"
        :allow-unidentified="allowUnidentified"
        :unidentified-locked="unidentifiedLocked"
        :can-open-schedule-settings="canOpenScheduleSettings"
        @open-schedule-settings="openScheduleSettings(close)"
      />
      <CoverageResponsibleField
        v-if="!release && replacement === CoverageMode.UNIDENTIFIED"
        v-model:responsible-id="coverageResponsibleId"
        v-model:note="coverageNote"
        :health-unit-id="props.stationDetails?.schedule?.healthUnit?.id ?? null"
        :health-unit-name="props.stationDetails?.schedule?.healthUnit?.name ?? null"
        @update:responsible-name="selectedResponsibleName = $event"
      />

      <p class="text-left text-textPrimary mb-2">Opções de remoção</p>
      <div class="flex flex-col gap-6">
        <div class="flex flex-col gap-2">
          <v-autocomplete
            v-model="selectedRecurrence"
            :items="removeRecurrenceOptions"
            label="Recorrência da remoção"
            variant="outlined"
            color="primary20"
            density="comfortable"
            item-title="title"
            item-value="value"
            :loading="isLoading"
            hide-details
          />
          <p class="text-left text-sm text-[#68688D]">
            {{ recurrenceDescription }}
          </p>
        </div>

        <div v-if="selectedRecurrence !== 'once'" class="flex flex-col gap-6">
          <OptionSelect
            v-if="selectedRecurrence === 'abcde'"
            label="Selecione as semanas do mês a serem removidas"
            :options="weekOptions"
            v-model="selectedWeeks"
            multiple
          />

          <OptionSelect
            label="Selecione os dias da semana a serem removidos"
            :options="dayOptions"
            v-model="selectedDay"
            multiple
          />

          <div>
            <p class="text-left text-textPrimary mb-4">Abrangência</p>
            <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
              <SelectableCard
                v-for="opt in removePeriodicityOptions"
                :key="opt.value"
                :title="opt.title"
                :description="opt.description"
                :selected="selectedPeriodicity === opt.value"
                @click="selectedPeriodicity = opt.value"
                class="!flex"
              />
            </div>

            <div
              class="mt-4 flex items-start gap-2 p-3 rounded-md bg-primary/5 border border-primary/20"
            >
              <v-icon :icon="mdiInformationOutline" class="text-primary w-5 h-5 mt-0.5 shrink-0" />
              <p class="text-sm text-textPrimary">
                A abrangência selecionada será aplicada a partir da data deste plantão
                <span class="font-semibold">({{ formattedStartDate }})</span>
                até o limite definido na opção abaixo.
              </p>
            </div>
          </div>

          <v-row>
            <div class="flex w-full gap-4">
              <div :class="selectedRepetition === 'personalized' ? 'w-3/4' : 'w-full'">
                <v-autocomplete
                  v-model="selectedRepetition"
                  :items="repetition"
                  label="Se repete até"
                  variant="outlined"
                  color="primary20"
                  density="comfortable"
                  item-title="title"
                  item-value="value"
                  :loading="isLoading"
                  hide-details
                />
              </div>

              <div v-if="selectedRepetition === 'personalized'" class="w-1/4">
                <DatePicker
                  v-model="startDate"
                  :max-date="
                    props.stationDetails?.schedule?.endDate
                      ? new Date(props.stationDetails.schedule.endDate)
                      : null
                  "
                  :initial-page="initialPage"
                  locale="pt-BR"
                  :masks="{ input: 'DD/MM/YYYY' }"
                >
                  <template #default="{ inputValue, inputEvents }">
                    <v-text-field
                      label="Data final"
                      :model-value="inputValue"
                      v-on="inputEvents"
                      color="primary20"
                      variant="outlined"
                      density="comfortable"
                      :prepend-inner-icon="mdiCalendar"
                      readonly
                      hide-details
                    />
                  </template>
                </DatePicker>
              </div>
            </div>
          </v-row>
        </div>
      </div>
    </div>

    <div v-else>
      <div v-if="totalShiftsCount > 0" class="flex flex-col gap-4">
        <p class="text-sm text-left text-textPrimary" data-testid="remove-doctor-summary">
          {{ summaryText }}
        </p>
        <!-- Com bloqueio, mostra contadores, o que é ignorado e a orientação "O que fazer" (M6-C). -->
        <CoveragePreviewSummary
          v-if="coveragePlan.hasContract && coveragePlan.counts.BLOCKED > 0"
          :plan="coveragePlan"
        />
        <ManagementDoctorTable
          :headers="previewHeaders"
          :items="limitedFormattedShifts"
          expandable
          @expand="handleExpandTable"
        />
      </div>
      <div v-else class="flex flex-col items-center justify-center text-center py-10">
        <v-icon class="w-24 h-24 mb-4" :icon="mdiAlertCircleOutline" :color="'#DA1E28'" />
        <p class="text-base text-gray-600">
          Nenhum plantão encontrado com os parâmetros selecionados.
        </p>
      </div>
    </div>

    <template #footer>
      <div class="p-4">
        <template v-if="!showShiftList">
          <div class="flex flex-col-reverse md:flex-row gap-3 justify-between w-full">
            <OutlineButton height="48px" color="grey" @click="close" class="w-full md:w-auto">
              Cancelar
            </OutlineButton>
            <FilledButton
              height="48px"
              color="primary"
              data-testid="remove-doctor-view-shifts-button"
              @click="handleViewDoctorStations"
              :loading="isLoadingShifts"
              :disabled="!isViewShiftsButtonEnabled"
              class="w-full md:w-auto"
            >
              Ver plantões
            </FilledButton>
          </div>
        </template>

        <template v-else>
          <template v-if="totalShiftsCount > 0">
            <div class="flex flex-col-reverse md:flex-row gap-3 justify-between w-full">
              <OutlineButton
                height="48px"
                @click="showShiftList = false"
                color="grey"
                class="w-full md:w-auto"
              >
                Voltar
              </OutlineButton>
              <OutlineButton
                height="48px"
                color="negative"
                class="!border-negative w-full md:w-auto"
                data-testid="remove-doctor-confirm-button"
                :disabled="isSubmitBlocked"
                @click="submitRemoveDoctor"
                :loading="isLoadingSubmit"
              >
                {{ confirmLabel }}
              </OutlineButton>
            </div>
          </template>
          <template v-else>
            <div class="flex justify-center w-full">
              <FilledButton
                height="48px"
                color="primary"
                @click="showShiftList = false"
                class="w-full md:w-auto"
              >
                Voltar
              </FilledButton>
            </div>
          </template>
        </template>
      </div>
    </template>
  </ModalVue>

  <ExpandedTableModal
    :showModal="isExpandedTableModalOpen"
    :headers="previewHeaders"
    :items="formattedShifts"
    :mode="'remove'"
    :title="release ? 'Liberar vaga' : null"
    :conflict-count="conflictShiftsCount"
    :open-count="coveragePlan.hasContract ? coveragePlan.counts.OPEN : openShiftsCount"
    :closed-count="coveragePlan.hasContract ? coveragePlan.counts.DOCTOR : closedShiftsCount"
    :unidentified-count="
      coveragePlan.hasContract ? coveragePlan.counts.UNIDENTIFIED : unregisteredShiftsCount
    "
    :blocked-count="coveragePlan.hasContract ? coveragePlan.counts.BLOCKED : null"
    :total-count="totalShiftsCount"
    @close="handleCloseExpandedTable"
  />

  <CoverageTransitionConfirmModal
    :show-modal="isTransitionModalOpen"
    :summary="transitionSummary"
    :responsible-name="selectedResponsibleName"
    :loading="isLoadingSubmit"
    @confirm="handleTransitionConfirm"
    @cancel="isTransitionModalOpen = false"
  />
</template>

<script lang="ts" setup>
import { ref, computed, PropType, watch, toRef } from 'vue'
import { useToast } from 'vue-toastification'
import { useErrorToast } from '@/composables/useErrorToast'
import { DatePicker } from 'v-calendar'
import { endOfDay, format } from 'date-fns'
import 'v-calendar/style.css'
import { mdiAlertCircleOutline, mdiCalendar, mdiInformationOutline } from '@mdi/js'
import StationService from '@/Network/Services/StationService'
import { ShiftDetails, StationFormSchedule } from '@/Network/Types/Responses/StationResponses'
import {
  ListStationsFromScheduleRequest,
  PatchStationsFromSchedulePayload,
  StationTypes
} from '@/Network/Types/Requests/StationRequests'
import ModalVue from '@/components/designSystem/modals/Modal.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import CardDutyWithDoctor from '@/components/designSystem/Cards/CardDutyWithDoctor.vue'
import ManagementDoctorTable from '../../components/ManagementDoctorTable.vue'
import ExpandedTableModal from '../../components/ExpandedTableModal.vue'
import CoveragePreviewSummary from '../../components/CoveragePreviewSummary.vue'
import SelectableCard from '@/components/designSystem/Cards/SelectableCard.vue'
import OptionSelect from '@/components/designSystem/Select/OptionSelect.vue'
import Spinner from '@/components/others/Spinner.vue'
import {
  dayOptions,
  repetition,
  weekOptions,
  removeRecurrenceOptions,
  removePeriodicityOptions,
  removeTableHeaders,
  dayMapping,
  weekMapping
} from '../utils/data'
import { useStationStore } from '@/pinia-store/useStationStore'
import {
  useExpandedTable,
  useFormattedShifts,
  useInitialPage,
  useSummaryText
} from '../utils/modalUtils'
import ActionModalHeader from '../../components/ActionModalHeader.vue'
import {
  getPostStationApprovedMessage,
  isPostStationApprovedError
} from '@/modules/company/PostStation/errors'
import CoverageModeSelector from '@/components/Coverage/CoverageModeSelector.vue'
import { useUnidentifiedCoverageLock } from '@/components/Coverage/useUnidentifiedCoverageLock'
import CoverageResponsibleField from '@/components/Coverage/CoverageResponsibleField.vue'
import CoverageTransitionConfirmModal from '@/components/Coverage/CoverageTransitionConfirmModal.vue'
import UnidentifiedCoverageCard from '@/components/Coverage/UnidentifiedCoverageCard.vue'
import { CoverageMode } from '@/Network/Types/Requests/CoverageRequests'
import {
  canMarkUnidentified,
  getStationCoverageInfo,
  scheduleSupportsCoverage
} from '@/utils/coverage'
import { getCoverageErrorMessage } from '@/utils/coverageErrors'
import {
  buildCoverageOperations,
  buildCoveragePatchStations,
  buildCoveragePlan,
  buildTransitionSummary,
  coverageTableHeaders,
  needsTransitionConfirmation,
  toPreviewTableRow
} from '../utils/coverageAllocation'

const props = defineProps({
  showModal: {
    type: Boolean,
    required: true
  },
  stationDetails: {
    type: Object as PropType<ShiftDetails | null>,
    default: () => null
  },
  // "Liberar vaga" (M7-B): parte de um plantão ocupado sem identificação e volta a vaga aberta.
  release: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['closeModal', 'removalUpdated'])
const toast = useToast()
const { notifyError } = useErrorToast()
const stationService = new StationService()
const stationStore = useStationStore()

const isThisModalVisible = ref(props.showModal)
const isExpandedTableModalOpen = ref(false)
const selectedPeriodicity = ref<number | null>(2)
const showShiftList = ref(false)
const isLoading = ref(false)
const selectedRecurrence = ref('once')
const selectedWeeks = ref(['A', 'C'])
const selectedDay = ref(['seg'])
const startDate = ref()
const selectedRepetition = ref('end_scale')

const shifts = ref<StationFormSchedule[]>([])
const isLoadingShifts = ref(false)
const isLoadingSubmit = ref(false)
const totalShiftsCount = ref(0)
const conflictShiftsCount = ref(0)
const openShiftsCount = ref(0)
const closedShiftsCount = ref(0)
// `countUnregistered` do BE: ocupado sem identificação saiu de `countOpen` (null = BE antigo).
const unregisteredShiftsCount = ref<number | null>(null)

const replacement = ref<CoverageMode>(CoverageMode.OPEN)
const coverageResponsibleId = ref<number | null>(null)
const coverageNote = ref('')
const selectedResponsibleName = ref<string | null>(null)
const isTransitionModalOpen = ref(false)

const scheduleSupports = computed(() => scheduleSupportsCoverage(props.stationDetails?.schedule))
const allowUnidentified = computed(() => canMarkUnidentified(props.stationDetails?.schedule))
const { unidentifiedLocked, canOpenScheduleSettings, openScheduleSettings } =
  useUnidentifiedCoverageLock(() => props.stationDetails?.schedule)
const originCoverage = computed(() => getStationCoverageInfo(props.stationDetails))
const target = computed(() => (props.release ? CoverageMode.OPEN : replacement.value))
const coveragePlan = computed(() =>
  buildCoveragePlan(shifts.value, target.value, {
    scope: props.release ? 'unidentifiedOnly' : 'doctorOnly'
  })
)
const transitionSummary = computed(() => buildTransitionSummary(coveragePlan.value))
const previewHeaders = computed(() =>
  coveragePlan.value.hasContract ? coverageTableHeaders : removeTableHeaders
)
// Com contrato e nenhuma linha aplicável (todas bloqueadas) não há o que enviar (M6-C).
const isSubmitBlocked = computed(
  () => coveragePlan.value.hasContract && coveragePlan.value.applicableRows.length === 0
)
const confirmLabel = computed(() => {
  if (props.release) return 'Confirmar liberação'
  // M4: com a prévia de cobertura o botão diz em quantos plantões vai aplicar.
  if (coveragePlan.value.hasContract) {
    const n = coveragePlan.value.applicableRows.length
    return n > 0 ? `Aplicar em ${n} ${n === 1 ? 'plantão' : 'plantões'}` : 'Aplicar'
  }
  return needsTransitionConfirmation(coveragePlan.value) ? 'Continuar' : 'Confirmar remoção'
})

const { handleExpandTable, handleCloseExpandedTable } = useExpandedTable(
  isThisModalVisible,
  isExpandedTableModalOpen
)

const recurrenceDescription = computed(() => {
  const selected = removeRecurrenceOptions.find((opt) => opt.value === selectedRecurrence.value)
  return selected ? selected.description : ''
})

const formattedStartDate = computed(() => {
  if (props.stationDetails && props.stationDetails.startHour) {
    try {
      return format(new Date(props.stationDetails.startHour), 'dd/MM/yyyy')
    } catch (e) {
      return ''
    }
  }
  return ''
})

const isViewShiftsButtonEnabled = computed(() => {
  if (target.value === CoverageMode.UNIDENTIFIED && !coverageResponsibleId.value) return false
  if (selectedRecurrence.value === 'once') {
    return true
  }
  return !!selectedPeriodicity.value && !!selectedRecurrence.value && selectedDay.value.length > 0
})

const ALL_BLOCKED_MESSAGES: Record<CoverageMode, string> = {
  [CoverageMode.DOCTOR]:
    'Nenhum plantão pode ser alterado: todos os selecionados estão bloqueados.',
  [CoverageMode.UNIDENTIFIED]:
    'Nenhum plantão pode ser marcado como ocupado sem identificação: todos os selecionados estão bloqueados.',
  [CoverageMode.OPEN]:
    'Nenhum plantão pode ser liberado como vaga aberta: todos os selecionados estão bloqueados.'
}

const doctorNameToUse = computed(() => props.stationDetails?.doctorStation?.doctor?.user?.name)
const summaryText = computed(() => {
  const n = coveragePlan.value.applicableRows.length
  const noun = n === 1 ? 'plantão' : 'plantões'
  if (coveragePlan.value.hasContract && n === 0 && coveragePlan.value.counts.BLOCKED > 0)
    return ALL_BLOCKED_MESSAGES[target.value]
  if (props.release)
    return `Você está liberando ${n} ${noun} ocupado(s) sem identificação. Eles voltam a ser vaga aberta.`
  if (target.value === CoverageMode.UNIDENTIFIED)
    return `Você está trocando o médico por "Ocupado sem identificação" em ${n} ${noun}. Deseja continuar?`
  return useSummaryText(doctorNameToUse, totalShiftsCount, 'remove')
})

const legacyFormattedShifts = useFormattedShifts(shifts, 'remove')
const formattedShifts = computed(() =>
  coveragePlan.value.hasContract
    ? coveragePlan.value.rows.map(toPreviewTableRow)
    : legacyFormattedShifts.value
)

const limitedFormattedShifts = computed(() => formattedShifts.value.slice(0, 9))

const initialPage = useInitialPage(toRef(props, 'stationDetails'))

const initializeDefaults = () => {
  let defaultDay = 'seg'
  let defaultWeek = 'A'

  if (props.stationDetails && props.stationDetails.startHour) {
    try {
      const date = new Date(props.stationDetails.startHour)
      if (!isNaN(date.getTime())) {
        const dayIndex = date.getDay()
        const foundKey = (Object.keys(dayMapping) as Array<keyof typeof dayMapping>).find(
          (key) => dayMapping[key] === dayIndex
        )
        if (foundKey) {
          defaultDay = String(foundKey)
        }

        const dayOfMonth = date.getDate()
        const weekIndex = Math.floor((dayOfMonth - 1) / 7)
        const weekLetters = ['A', 'B', 'C', 'D', 'E']
        if (weekIndex >= 0 && weekIndex < weekLetters.length) {
          defaultWeek = weekLetters[weekIndex]
        }
      }
    } catch (e) {
      console.error('Erro ao calcular data padrão da station:', e)
    }
  }

  selectedWeeks.value = [defaultWeek]
  selectedDay.value = [defaultDay]
}

// eslint-disable-next-line complexity
const buildDoctorStationsFilters = (): ListStationsFromScheduleRequest | null => {
  if (!props.stationDetails?.schedule?.id) return null
  // Backend com a feature recebe o alvo da cobertura para calcular bloqueios do preview
  // (ex.: ANTECIPE ao trocar o médico por "Ocupado sem identificação").
  const coverageFilter = scheduleSupports.value ? { coverage: target.value } : {}

  if (selectedRecurrence.value === 'once') {
    return {
      schedule: props.stationDetails.schedule.id,
      station: props.stationDetails.id,
      ...coverageFilter
    }
  }

  const sDate = new Date(props.stationDetails.startHour)
  let eDate: Date | undefined

  if (selectedRepetition.value === 'personalized' && startDate.value) {
    eDate = endOfDay(startDate.value)
  }

  if (selectedRepetition.value === 'end_scale' && props.stationDetails.schedule.endDate) {
    eDate = new Date(props.stationDetails.schedule.endDate)
  }

  const weekDays = selectedDay.value.map((day) => dayMapping[day]).join(',')

  let weekOfMonth = '0,1,2,3,4,5'

  let fortnightlyFilters = {}
  if (selectedRecurrence.value === 'fortnightly') {
    fortnightlyFilters = {
      alternatingWeek: true,
      stationWeekParity: props.stationDetails.stationWeekParity
    }
  }

  if (selectedRecurrence.value === 'abcde') {
    weekOfMonth = selectedWeeks.value.map((week) => weekMapping[week]).join(',')
  }

  const filters: ListStationsFromScheduleRequest = {
    schedule: props.stationDetails.schedule.id,
    startDate: sDate.toISOString(),
    weekDays: weekDays,
    weekOfMonth: weekOfMonth,
    ...fortnightlyFilters
  }

  if (selectedPeriodicity.value === 1 && props.stationDetails?.doctorStation?.doctor?.id) {
    filters.doctorStationPackage = props.stationDetails.doctorStation?.package?.id
  }

  if (selectedPeriodicity.value === 2) {
    filters.position = props.stationDetails.position.id
    filters.shifts = [props.stationDetails.shift.id].join(',')
  }

  if (eDate) {
    filters.endDate = eDate.toISOString()
  }

  return { ...filters, ...coverageFilter }
}

async function handleViewDoctorStations() {
  const baseFilters = buildDoctorStationsFilters()
  if (!baseFilters) {
    toast.error('Detalhes da escala não encontrados para buscar os plantões.')
    return
  }

  isLoadingShifts.value = true
  shifts.value = []
  try {
    const response = await stationService.listStationsFromSchedule({
      ...baseFilters,
      excludeHasCheckIn: true
    })

    shifts.value = response.stations
    totalShiftsCount.value = response.count
    openShiftsCount.value = response.countOpen
    conflictShiftsCount.value = response.countConflict
    closedShiftsCount.value = response.countClosed
    unregisteredShiftsCount.value = response.countUnregistered ?? null
    showShiftList.value = true
  } catch (error) {
    notifyError(error, 'Não foi possível carregar a lista de plantões')
    console.error(error)
  } finally {
    isLoadingShifts.value = false
  }
}

async function submitRemoveDoctor() {
  // M6: médico real → sem identificação passa pela confirmação antes de gravar.
  if (needsTransitionConfirmation(coveragePlan.value)) {
    isTransitionModalOpen.value = true
    return
  }
  await executeRemoval()
}

async function handleTransitionConfirm() {
  // M6 só fecha no sucesso: se o PATCH falhar, o usuário continua na confirmação (com o toast de erro).
  const succeeded = await executeRemoval()
  if (succeeded) isTransitionModalOpen.value = false
}

// eslint-disable-next-line complexity
async function executeRemoval(): Promise<boolean> {
  isLoadingSubmit.value = true
  try {
    const plan = coveragePlan.value
    const operations = buildCoverageOperations(plan, {
      fallbackOriginDoctorId: props.stationDetails?.doctorStation?.doctor?.id ?? null,
      packageId: props.stationDetails?.doctorStation?.package?.id ?? null
    })
    if (shifts.value.length === 0 || operations.length === 0) {
      toast.error('Nenhum plantão selecionado para remoção.')
      return false
    }

    const payload: PatchStationsFromSchedulePayload = {
      operations,
      patchStations: buildCoveragePatchStations({
        target: target.value,
        hasContract: plan.hasContract,
        doctorId: null,
        ourTeam: false,
        userSponsorId: null,
        formationId: null,
        stationType:
          (props.stationDetails?.doctorStation?.type as StationTypes | undefined) ?? null,
        responsibleId: coverageResponsibleId.value,
        note: coverageNote.value
      }),
      scheduleId: Number(props.stationDetails?.schedule?.id)
    }

    await stationService.patchStationsFromSchedule(payload)

    toast.success(
      props.release
        ? 'Vaga liberada com sucesso!'
        : target.value === CoverageMode.UNIDENTIFIED
          ? 'Plantões marcados como ocupados sem identificação.'
          : 'Remoção confirmada com sucesso!'
    )
    emit('removalUpdated')
    close()
    await stationStore.getListStations()
    return true
  } catch (error) {
    console.error('Erro ao submeter a remoção:', error)

    // Defesa em profundidade (RN-07 / RF-12): o botão que abre este modal já
    // vem desabilitado num plantão aprovado, mas a varredura pode aprovar
    // enquanto o modal está aberto. Sem este ramo o 400 do backend aparecia
    // como "Erro ao confirmar a remoção.", que não diz o que fazer. O backend
    // manda a frase pronta em pt-BR — repassar em vez de reescrever.
    if (isPostStationApprovedError(error)) {
      toast.error(getPostStationApprovedMessage(error))
      return false
    }

    // Códigos de cobertura (Antecipe, check-in, flag desligada…) viram a frase do catálogo;
    // demais erros seguem o toast genérico (antes: "Erro ao confirmar a remoção." estático).
    const coverageMessage = getCoverageErrorMessage(error)
    if (coverageMessage) {
      toast.error(coverageMessage)
    } else {
      notifyError(error, 'Não foi possível confirmar a remoção')
    }
    return false
  } finally {
    isLoadingSubmit.value = false
  }
}

function close() {
  emit('closeModal')
}

watch(
  () => props.stationDetails,
  (newDetails) => {
    if (newDetails && props.showModal) {
      initializeDefaults()
    }
  },
  { immediate: true, deep: true }
)

watch(
  () => props.showModal,
  (newValue) => {
    isThisModalVisible.value = newValue
    showShiftList.value = false
    shifts.value = []
    totalShiftsCount.value = 0
    openShiftsCount.value = 0
    closedShiftsCount.value = 0

    if (newValue) {
      initializeDefaults()
      selectedPeriodicity.value = 2
      selectedRepetition.value = 'end_scale'
      replacement.value = CoverageMode.OPEN
      coverageResponsibleId.value = null
      coverageNote.value = ''
      selectedResponsibleName.value = null
      isTransitionModalOpen.value = false
    } else {
      isExpandedTableModalOpen.value = false
    }
  }
)
</script>
